import { toGenericPriceKey, toOracleKey } from '../../assets'
import { isUnearnableRate } from '../rateSanity'
import { GenericTokenList, MulticallRetryFunction } from '../../types'
import { apyToAprPercent } from '../../utils/parsing'
import { fetchAssetDecimals } from '../assetDecimals'
import {
  assetKey,
  fetchPendleApiAssets,
  fetchPendleApiMarkets,
  hasPendleMarkets,
  isLiveMarket,
  parseExpirySeconds,
  splitChainScopedAddress,
  type PendleApiAsset,
  type PendleApiMarket,
} from './api'
import { PendlePtMarket, PendlePtMarkets } from './types'

const SECONDS_PER_DAY = 86_400

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** A FRACTION from the API (`0.0721`) → a percent (`7.21`). */
const fractionToPercent = (v: number | null | undefined): number | null => {
  const n = num(v)
  return n === null ? null : n * 100
}

export interface FetchPendlePtOptions {
  /**
   * Include markets that have already matured.
   *
   * **Default `false`, and that default is the point of this provider.** An
   * expired PT redeems at par, so its forward yield is zero — but every rate
   * field the API and the token list carry still holds the last pre-expiry
   * value. Serving those rows would put a stale fixed APY at the top of an
   * APR-sorted earn list on a product that no longer exists.
   *
   * The escape hatch exists for the same reason yield-tracer's `/assets`
   * route has one: a user who HOLDS a matured PT still needs the row to
   * redeem it. Mirrors `?includeExpired=` there, including the name.
   */
  includeExpired?: boolean
  /**
   * Clock override, unix seconds. Tests only — production always judges
   * expiry against the real clock, never against a cached flag.
   */
  nowSecs?: number
}

/**
 * Token metadata resolved from the first source that has it.
 *
 * Order is deliberate: the caller's token list wins (it carries our curated
 * `assetGroup`, which is how the price store is keyed), then Pendle's own
 * asset listing, then an on-chain `decimals()` read. Anything still unknown
 * means the row is dropped.
 */
interface ResolvedToken {
  decimals?: number
  symbol?: string
  name?: string
  logoURI?: string
  assetGroup?: string
}

function resolveToken(
  address: string,
  chainId: string,
  tokenList: GenericTokenList,
  apiAssets: Map<string, PendleApiAsset>,
  decimalsOnChain: Map<string, number>,
): ResolvedToken {
  const listed = tokenList[address]
  const api = apiAssets.get(assetKey(chainId, address))
  const apiDecimals =
    typeof api?.decimals === 'number' && Number.isFinite(api.decimals)
      ? api.decimals
      : undefined
  return {
    decimals: listed?.decimals ?? apiDecimals ?? decimalsOnChain.get(address),
    symbol: listed?.symbol ?? api?.symbol ?? undefined,
    name: listed?.name ?? api?.name ?? undefined,
    logoURI: listed?.logoURI ?? api?.proIcon ?? undefined,
    assetGroup: listed?.assetGroup,
  }
}

/**
 * One API market → a `PendlePtMarket`, or `null` when it cannot be modelled.
 *
 * Drops (rather than patches) any market missing a PT address, an underlying,
 * a parseable expiry, or PT decimals — see `PendlePtMarket.decimals` for why
 * defaulting the last one is worse than dropping the row.
 */
function parseMarket(
  m: PendleApiMarket,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  apiAssets: Map<string, PendleApiAsset>,
  decimalsOnChain: Map<string, number>,
  nowSecs: number,
): PendlePtMarket | null {
  const pt = splitChainScopedAddress(m.pt)
  const underlying = splitChainScopedAddress(m.underlyingAsset)
  const marketAddress = (m.address ?? '').toLowerCase()
  if (!pt || !underlying || !marketAddress) return null

  const expiry = parseExpirySeconds(m.expiry)
  if (expiry === undefined) return null

  const ptMeta = resolveToken(
    pt.address,
    chainId,
    tokenList,
    apiAssets,
    decimalsOnChain,
  )
  const assetMeta = resolveToken(
    underlying.address,
    chainId,
    tokenList,
    apiAssets,
    decimalsOnChain,
  )

  // Pendle's `/assets/all` carries ONLY `PT` / `YT` / `SY` / `PENDLE_LP`
  // entries — it never lists an UNDERLYING — so tier 2 can name the PT but can
  // never name what the PT redeems FOR. Where the token list has no entry
  // either, the asset reached the listing with an empty symbol and rendered as
  // a raw address: 29 of 77 live markets (sUSD3, STRCx, trUSD, jrUSDat,
  // nOPAL…), i.e. every underlying too new or too niche to be curated.
  //
  // The market's own `name` IS Pendle's label for that underlying — that is
  // what the field means (see `PendleApiMarket.name`) and it holds on every
  // unresolved market — and `icon` is that asset's icon. Neither is the PT's
  // own symbol, which is the one thing that must NOT be used here: labelling
  // the row with the wrapper would claim the position is backed by itself.
  const apiName = typeof m.name === 'string' && m.name ? m.name : undefined
  const assetSymbol = assetMeta.symbol ?? apiName
  const assetName = assetMeta.name ?? apiName
  const assetLogoURI =
    assetMeta.logoURI ??
    (typeof m.icon === 'string' && m.icon ? m.icon : undefined)

  // Never the underlying's decimals as a fallback for the PT's — they disagree
  // on ~16 % of live markets, and the error is silent.
  const decimals = ptMeta.decimals
  if (decimals === undefined) return null

  const assetDecimals = assetMeta.decimals ?? decimals

  const details = m.details ?? {}

  // `impliedApy` is a compounded APY fraction; the package convention is a
  // nominal APR percent, so convert. `impliedApyPercent` keeps Pendle's own
  // number so a UI can show what pendle.finance shows.
  const impliedApyPercent = fractionToPercent(details.impliedApy) ?? 0
  const supplyRate = apyToAprPercent(impliedApyPercent)
  const underlyingApyPercent = fractionToPercent(details.underlyingApy)

  const totalAssetsUsd = num(details.totalTvl) ?? 0
  const liquidityUsd = num(details.liquidity) ?? 0

  const priceKey =
    toOracleKey(assetMeta.assetGroup ?? null) ||
    toGenericPriceKey(underlying.address, chainId)
  const priceUsd = prices[priceKey] ?? prices[underlying.address]

  // The PT trades at a DISCOUNT to the underlying — that discount IS the
  // yield — so the two prices are looked up separately and never substituted
  // for one another.
  const ptPriceKey =
    toOracleKey(ptMeta.assetGroup ?? null) ||
    toGenericPriceKey(pt.address, chainId)
  const ptPriceUsd = prices[ptPriceKey] ?? prices[pt.address]

  const secondsToExpiry = Math.max(expiry - nowSecs, 0)
  const yt = splitChainScopedAddress(m.yt)
  const sy = splitChainScopedAddress(m.sy)

  return {
    address: pt.address,
    marketAddress,
    underlying: underlying.address,
    ytAddress: yt?.address,
    syAddress: sy?.address,

    symbol: ptMeta.symbol ?? `PT-${m.name ?? ''}`,
    name: ptMeta.name ?? `PT ${m.name ?? ''}`,
    decimals,
    assetDecimals,

    expiry,
    expiryIso: m.expiry ?? new Date(expiry * 1000).toISOString(),
    secondsToExpiry,
    daysToExpiry: Math.round((secondsToExpiry / SECONDS_PER_DAY) * 100) / 100,

    supplyRate,
    rewardsRate: 0,
    depositRate: supplyRate,
    impliedApyPercent,
    underlyingApyPercent: underlyingApyPercent ?? undefined,
    feeRate: num(details.feeRate) ?? undefined,

    totalAssetsUsd,
    totalAssetsFormatted: totalAssetsUsd,
    liquidityUsd,

    isMintable: true,
    withdrawalMode: 'market-sale',

    // Pre-seeded from Pendle's own branded PT icon. `stampVaultClassification`
    // overwrites it when the token list resolves something, and leaves it
    // alone otherwise — so a row still renders an icon with no token list.
    logoURI: ptMeta.logoURI,

    // Prefer the curated token-list entry (it carries `assetGroup`, tags and
    // everything else downstream reads); synthesize a minimal one from
    // Pendle's metadata otherwise, so `stampVaultClassification` still has a
    // symbol to read for the stable/volatile call.
    asset:
      tokenList[underlying.address] ??
      (assetSymbol || assetMeta.decimals !== undefined
        ? {
            chainId,
            address: underlying.address,
            symbol: assetSymbol,
            name: assetName,
            decimals: assetDecimals,
            logoURI: assetLogoURI,
          }
        : undefined),
    priceUsd,
    ptPriceUsd,
    categoryIds: m.categoryIds ?? undefined,
    protocol: m.protocol ?? undefined,
  }
}

/**
 * Fetch every LIVE Pendle PT market on a chain, modelled as fixed-rate earn
 * products.
 *
 * **HTTP-only in every normal case.** Token metadata resolves in three tiers —
 * the caller's token list, then Pendle's own global asset listing, then an
 * on-chain `decimals()` multicall — and each tier is consulted only for what
 * the previous one could not answer. A caller with a hydrated token list makes
 * exactly ONE request (the shared, cached markets listing); a caller with none
 * (the worker's `?source=live` path passes `{}`) makes two, and still never
 * touches an RPC.
 *
 * The multicall is a last resort on purpose. Reading `decimals()` for ~110 PTs
 * on Ethereum makes the entire chain's listing hostage to one multicall, and
 * viem's `allowFailure` returns `'0x'` per call instead of throwing when the
 * transport dies — so the failure mode is a silent, complete disappearance of
 * Pendle on the busiest chain rather than an error anyone would notice.
 *
 * @param chainId         target chain
 * @param multicallRetry  last-resort decimals gap-fill only; usually unused
 * @param prices          price map keyed by oracle key / address
 * @param tokenList       token list for PT + underlying metadata
 * @param options         `includeExpired` and a test clock — see
 *                        {@link FetchPendlePtOptions}
 *
 * @returns map keyed by lowercased PT address; empty on chains without a
 *          Pendle deployment.
 */
export const fetchPendlePtMarkets = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  options: FetchPendlePtOptions = {},
): Promise<PendlePtMarkets> => {
  if (!hasPendleMarkets(chainId)) return {}

  const nowSecs = options.nowSecs ?? Math.floor(Date.now() / 1000)
  const all = await fetchPendleApiMarkets()
  const want = Number(chainId)

  const candidates = all.filter((m) => {
    if (Number(m?.chainId) !== want) return false
    return options.includeExpired === true || isLiveMarket(m, nowSecs)
  })
  if (candidates.length === 0) return {}

  /** Addresses whose decimals are still unknown after the tiers so far. */
  const stillUnknown = (known: (a: string) => boolean): string[] => {
    const out = new Set<string>()
    for (const m of candidates) {
      for (const ref of [m.pt, m.underlyingAsset]) {
        const parsed = splitChainScopedAddress(ref)
        if (parsed && !known(parsed.address)) out.add(parsed.address)
      }
    }
    return [...out]
  }

  // Tier 2 — Pendle's own asset metadata, but only if the token list left a
  // gap. A fully-hydrated caller never pays for this request.
  let apiAssets = new Map<string, PendleApiAsset>()
  const listCovers = (a: string) => tokenList[a]?.decimals !== undefined
  if (stillUnknown(listCovers).length > 0) {
    try {
      apiAssets = await fetchPendleApiAssets()
    } catch {
      apiAssets = new Map()
    }
  }

  // Tier 3 — on-chain, for anything neither source knows. Normally empty.
  const httpCovers = (a: string) =>
    listCovers(a) ||
    typeof apiAssets.get(assetKey(chainId, a))?.decimals === 'number'
  const unresolved = stillUnknown(httpCovers)

  let decimalsOnChain = new Map<string, number>()
  if (unresolved.length > 0) {
    try {
      decimalsOnChain = await fetchAssetDecimals(
        chainId,
        unresolved,
        multicallRetry,
      )
    } catch {
      // A failed gap-fill costs us the unresolved markets, not the resolved
      // ones — `parseMarket` drops what it cannot scale. Never default.
      decimalsOnChain = new Map()
    }
  }

  const out: PendlePtMarkets = {}
  for (const m of candidates) {
    const parsed = parseMarket(
      m,
      chainId,
      prices,
      tokenList,
      apiAssets,
      decimalsOnChain,
      nowSecs,
    )
    if (!parsed) continue
    // Same annualization trap as Spectra's book, guarded before it bites: a
    // PT hours from maturity on dust turns sub-basis-point price noise into a
    // large APR of either sign. Pendle is clean today (56 markets, none
    // caught) — the check is here so it stays that way, and it only fires
    // when BOTH the size and the implied deviation say noise.
    if (
      isUnearnableRate({
        aprPercent: parsed.supplyRate,
        totalAssetsUsd: parsed.totalAssetsUsd,
        expiry: parsed.expiry,
        nowSecs,
      })
    ) {
      continue
    }
    out[parsed.address] = parsed
  }
  return out
}
